import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslations } from "@/i18n/IntlProvider";

/**
 * /$locale/admin/accounts — system-admin operator surface for browsing
 * all accounts (ADR-024 Tier 1, ADR-027 sub-iter #5 port from
 * web/src/components/admin/admin-accounts-list.tsx).
 *
 * Same-origin cookie auth: every request hits the BFF via the relative
 * /v1/admin/accounts path, the wrapper in @/lib/api sends the session
 * cookie + CSRF token automatically. The role-guard sits in
 * router.tsx; non-admins never reach this component.
 */
type AdminAccount = {
  id: string;
  displayName: string;
  primaryEmail: string;
  createdAt: string;
  archivedAt: string | null;
  suspended: boolean;
};

const PAGE_LIMIT = 100;

export function AdminAccountsRoute() {
  const { locale } = useParams({ strict: false }) as { locale?: string };
  const lang = locale ?? "de";
  const t = useTranslations();
  const [query, setQuery] = useState("");

  const accountsQuery = useQuery({
    queryKey: ["admin", "accounts", query],
    queryFn: () => {
      const params = new URLSearchParams();
      const trimmed = query.trim();
      if (trimmed.length > 0) params.set("q", trimmed);
      params.set("limit", String(PAGE_LIMIT));
      return api<AdminAccount[]>(`/v1/admin/accounts?${params.toString()}`);
    },
    placeholderData: (prev) => prev,
  });

  const accounts = accountsQuery.data ?? [];

  return (
    <section className="mx-auto max-w-screen-xl space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("app.admin.accounts.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("app.admin.accounts.subtitle").replace("{limit}", String(PAGE_LIMIT))}
          </p>
        </div>
        <input
          type="search"
          placeholder={t("app.admin.accounts.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 w-64 rounded-md border border-border bg-background px-3 text-sm"
        />
      </header>

      {accountsQuery.isPending && (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      )}
      {accountsQuery.isError && (
        <p className="text-sm text-destructive">
          {t("common.error")}: {(accountsQuery.error as Error).message}
        </p>
      )}
      {accountsQuery.data && <AccountsTable accounts={accounts} locale={lang} />}
    </section>
  );
}

function AccountsTable({
  accounts,
  locale,
}: {
  accounts: readonly AdminAccount[];
  locale: string;
}) {
  const t = useTranslations();
  if (accounts.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("app.admin.accounts.empty")}</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">{t("app.admin.accounts.table.email")}</th>
            <th className="px-3 py-2 text-left">{t("app.admin.accounts.table.displayName")}</th>
            <th className="px-3 py-2 text-left">{t("app.admin.accounts.table.created")}</th>
            <th className="px-3 py-2 text-left">{t("app.admin.accounts.table.status")}</th>
            <th className="px-3 py-2 text-left">{t("app.admin.accounts.table.id")}</th>
            <th className="px-3 py-2 text-right">
              <span className="sr-only">{t("app.admin.accounts.table.openLabel")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr
              key={a.id}
              className="group cursor-pointer border-t border-border hover:bg-accent/40"
            >
              <td className="px-3 py-2">
                <Link
                  to={"/$locale/admin/accounts/$id" as never}
                  params={{ locale, id: a.id } as never}
                  className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {a.primaryEmail}
                </Link>
              </td>
              <td className="px-3 py-2">{a.displayName}</td>
              <td className="px-3 py-2 tabular-nums text-muted-foreground">
                {new Date(a.createdAt).toLocaleString(locale, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </td>
              <td className="px-3 py-2">
                <StatusBadges account={a} />
              </td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                {a.id.slice(0, 8)}…
              </td>
              <td className="px-3 py-2 text-right">
                <Link
                  to={"/$locale/admin/accounts/$id" as never}
                  params={{ locale, id: a.id } as never}
                  aria-label={t("app.admin.accounts.table.openLabel")}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground group-hover:border-primary/40 group-hover:text-foreground"
                >
                  {t("app.admin.accounts.table.openCta")}
                  <ChevronRight aria-hidden className="size-3" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadges({ account }: { account: AdminAccount }) {
  const t = useTranslations();
  const flags: { key: "active" | "suspended" | "archived"; tone: "ok" | "warn" | "danger" }[] = [];
  if (account.suspended) flags.push({ key: "suspended", tone: "danger" });
  if (account.archivedAt) flags.push({ key: "archived", tone: "warn" });
  if (flags.length === 0) flags.push({ key: "active", tone: "ok" });
  return (
    <span className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span
          key={f.key}
          className={
            "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase " +
            (f.tone === "danger"
              ? "bg-rose-100 text-rose-900"
              : f.tone === "warn"
                ? "bg-amber-100 text-amber-900"
                : "bg-emerald-100 text-emerald-900")
          }
        >
          {t(`app.admin.accounts.status.${f.key}`)}
        </span>
      ))}
    </span>
  );
}
